import apiClient from './api';
import { OrderPlacementRequest, OrderSide } from '../types/preset'; // Import request type
import {
  OpenOrdersResponse,
  OrderHistoryResponse,
  OrderHistoryEntry,
  OpenOrder,
} from '../types/order'; // Import the types we just defined

// Define the expected response structure from the backend
// (Matches backend OrderPlacementResponseSchema)
interface OrderPlacementResponse {
  success: boolean;
  message: string;
  order_id?: string | null;
  status?: string;
}

// Define the cancel orders request structure
interface CancelOrdersRequest {
  symbol: string;
  side?: OrderSide;
}

// Define the cancel orders response structure
interface CancelOrdersResponse {
  success: boolean;
  message: string;
  cancelled_count?: number; 
}

const BASE_URL = '/order'; // Base URL for order endpoints

export const orderService = {
  /**
   * Places an order based on the provided form data.
   * @param orderData - The order details conforming to OrderPlacementRequest.
   * @returns The API response indicating success or failure.
   */
  placeOrderFromForm: async (
    orderData: OrderPlacementRequest
  ): Promise<OrderPlacementResponse> => {
    try {
      const response = await apiClient.post<OrderPlacementResponse>(
        `${BASE_URL}/place`, 
        orderData
      );
      return response.data;
    } catch (error: any) {
      console.error('Error placing order:', error.response?.data || error.message);
      // Re-throw the error so the component can handle it (e.g., show message)
      // Or return a specific error structure
      throw error; 
    }
  },

  /**
   * Alias for placeOrderFromForm for backwards compatibility
   */
  placeOrder: async (orderData: OrderPlacementRequest): Promise<OrderPlacementResponse> => {
    return orderService.placeOrderFromForm(orderData);
  },

  /**
   * Cancels multiple orders based on symbol and optional side.
   * @param request - Object containing symbol and optional side
   * @returns Promise resolving to the cancellation result
   */
  cancelOrders: async (request: CancelOrdersRequest): Promise<CancelOrdersResponse> => {
    try {
      const response = await apiClient.post<CancelOrdersResponse>(
        `${BASE_URL}/cancel/all`, 
        request
      );
      return response.data;
    } catch (error: any) {
      console.error('Error cancelling orders:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Fetches the list of open orders.
   */
  getOpenOrders: async (): Promise<OpenOrder[]> => {
    try {
      const response = await apiClient.get<OpenOrdersResponse>(`${BASE_URL}/open`);
      return response.data.orders; // Extract the orders array
    } catch (error) {
      console.error('API Error fetching open orders:', error);
      // Re-throw or handle as needed for UI display
      throw new Error('Failed to fetch open orders.');
    }
  },

  /**
   * Fetches the order history (fills).
   * @param symbol Optional symbol to filter history by.
   * @param limit Maximum number of history entries to return.
   */
  getOrderHistory: async (
    symbol?: string | null,
    limit: number = 50
  ): Promise<OrderHistoryEntry[]> => {
    try {
      const params = new URLSearchParams();
      if (symbol) {
        params.append('symbol', symbol);
      }
      params.append('limit', limit.toString());

      const response = await apiClient.get<OrderHistoryResponse>(
        `${BASE_URL}/history`,
        { params } // Pass query parameters
      );
      return response.data.history; // Extract the history array
    } catch (error) {
      console.error('API Error fetching order history:', error);
      // Re-throw or handle as needed for UI display
      throw new Error('Failed to fetch order history.');
    }
  },

  /**
   * Cancels an open order.
   * @param symbol - The symbol of the order to cancel.
   * @param orderId - The ID of the order to cancel.
   * @returns Promise resolving to the cancellation result from the API.
   */
  cancelOrder: async (symbol: string, orderId: string): Promise<any> => { // Replace 'any' with a specific type if available
    try {
      // Construct the payload or URL parameters as required by the backend endpoint
      // Example using DELETE with orderId in the path:
      // const response = await apiClient.delete(`/order/cancel/${symbol}/${orderId}`);
      
      // Example using POST with data in the body:
      const response = await apiClient.post('/order/cancel', { symbol, order_id: orderId });
      
      // TODO: Define a specific type for the cancellation response
      return response.data; 
    } catch (error) {
      console.error(`Error cancelling order ${orderId} for symbol ${symbol}:`, error);
      // Re-throw a more specific error or handle as needed
      throw new Error(`Failed to cancel order ${orderId}`);
    }
  },
};